import React, { createContext, useContext, useEffect, useState } from 'react';

export function createFocusRescue() {
  const FocusRescueContext = createContext<FocusRescueManager | null>(null);

  function useFocusRescueManager() {
    const result = useContext(FocusRescueContext);
    if (!result) {
      throw new Error('FocusRescue.* can only be used inside a FocusRescue');
    }
    return result;
  }

  function FocusRescue(props: { children: React.ReactNode }) {
    const [focusRescueManager] = useState(() => new FocusRescueManager());

    useEffect(() => focusRescueManager.listen(), [focusRescueManager]);

    return (
      <FocusRescueContext.Provider value={focusRescueManager}>
        {props.children}
      </FocusRescueContext.Provider>
    );
  }

  function Rescuee(props: { children: React.ReactNode }) {
    const focusRescueManager = useFocusRescueManager();

    return (
      <div
        css={{ display: 'contents' }}
        ref={el => {
          if (el) {
            focusRescueManager.registerRescuee(el);
          }
        }}
      >
        {props.children}
      </div>
    );
  }

  function Rescuer(props: { children: React.ReactNode }) {
    const focusRescueManager = useFocusRescueManager();

    return (
      <div
        ref={el => {
          if (el) {
            focusRescueManager.registerRescuer(el);
            focusRescueManager.attemptRescueIfNeeded();
          }
        }}
        css={{ display: 'contents' }}
      >
        {props.children}
      </div>
    );
  }

  FocusRescue.Rescuee = Rescuee;
  FocusRescue.Rescuer = Rescuer;

  return FocusRescue;
}

class FocusRescueManager {
  private rescuee: Element | null = null;
  private rescuer: Element | null = null;
  private lastFocusedElement: Element | null = null;
  private userIsClickingAway = false;

  listen = () => {
    const focusin = (e: FocusEvent) => {
      this.userIsClickingAway = false;
      if (e.target instanceof Element) {
        this.lastFocusedElement = e.target;
      }
    };

    const focusout = (e: FocusEvent) => {
      // If e.relatedTarget is populated, that means focus is already moving to
      // a different element.  Therefore, we don't need to attempt a focus rescue
      // See: https://developer.mozilla.org/en-US/docs/Web/API/FocusEvent/relatedTarget
      //
      // Note: I tried to write a test for this in jest but I couldn't reproduce
      // the bug.  Seems like jsdom isn't exactly like the browser in this case
      if (!e.relatedTarget) {
        this.attemptRescueIfNeeded();
      }
    };

    const onMouseDown = () => {
      this.userIsClickingAway = true;
    };

    const onMouseUp = () => {
      this.userIsClickingAway = false;
    };

    document.addEventListener('focusin', focusin);
    document.addEventListener('focusout', focusout);
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('mouseup', onMouseUp);

    return () => {
      document.removeEventListener('focusin', focusin);
      document.removeEventListener('focusout', focusout);
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('mouseup', onMouseUp);
    };
  };

  registerRescuee(el: Element) {
    this.rescuee = el;
  }

  registerRescuer(el: Element) {
    this.rescuer = el;
  }

  attemptRescueIfNeeded() {
    // If the user is intentionally clicking away, then we shouldn't rescue the focus
    if (this.userIsClickingAway) {
      return;
    }

    // If the focus isn't lost, we don't need to attempt a rescue
    if (!isFocusLost()) {
      return;
    }

    // If there hasn't been an element focused yet, we don't need to attempt a rescue
    if (!this.lastFocusedElement) {
      return;
    }

    // If the rescuee does not contain the last focused element, then that means
    // this focus rescue is not applicable and we shouldn't try to rescue focus.
    // Hopefully there is a different focus rescue on the page that is
    // applicable that will rescue the focus instead
    if (!this.rescuee?.contains(this.lastFocusedElement)) {
      return;
    }

    // If the rescuer is no longer in the DOM, then we can't rescue the focus
    if (!this.rescuer?.isConnected) {
      return;
    }

    focusFirstFocusableChild(this.rescuer);
  }
}

function focusFirstFocusableChild(el: Element) {
  const firstFocusableChild = el.querySelector(
    'a[href], button, input, textarea, select, details, [tabindex]:not([tabindex="-1"])',
  );

  if (firstFocusableChild instanceof HTMLElement) {
    firstFocusableChild.focus();
  }
}

function isFocusLost() {
  return document.activeElement === document.body || !document.activeElement;
}
